import { Box } from '@mui/material';
import { useEffect, useState } from 'react';

import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const yesNoOptions = [
    {
        id: 'true',
        label: 'Ja',
    },
    {
        id: 'false',
        label: 'Nej',
    },
];

const NET_MONTHLY_INCOME = 'coapplicantNetMonthlyIncome';
const NEW_DEBT_THIS_YEAR_TOGGLE = 'coapplicantNewDebtThisYearToggle';
const NEW_DEBT_THIS_YEAR = 'coapplicantNewDebtThisYear';

/**
 * DK's co-applicant finance step, collecting `coapplicantNetMonthlyIncome` and `coapplicantNewDebtThisYear` --
 * mirrors the main-applicant `FinancePageDK`'s treatment of the same two attributes exactly (plain required
 * net-monthly-income field; a yes/no toggle that reveals a required amount field, cleared via `useFieldToggle`
 * on toggle-off).
 *
 * Replaces `CoApplicantIncomePage` for DK specifically: the `cl-common` PCN-620 rescope moved
 * `coapplicantYearlyGrossIncome`/`coapplicantMonthlyIncomeOther` (what that shared page collects) into NO's
 * own exclusive contract block and added `coapplicantNetMonthlyIncome`/`coapplicantNewDebtThisYear` for DK
 * instead -- DK can no longer legitimately compose the old pair. `CoApplicantIncomePage` itself is untouched
 * and still used by NO, where the old fields remain valid.
 */
const CoApplicantFinancePageDK = () => {
    useComponentTracker('CoApplicantFinancePageDK');

    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();

    useSetBannerType('bannerOne');

    const newDebtThisYearToggle = useAppSelector(selectFieldValue)(NEW_DEBT_THIS_YEAR_TOGGLE) === 'true';
    useFieldToggle(newDebtThisYearToggle, [NEW_DEBT_THIS_YEAR]);

    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    useEffect(() => {
        const fields = [NET_MONTHLY_INCOME, NEW_DEBT_THIS_YEAR_TOGGLE];
        if (newDebtThisYearToggle) {
            fields.push(NEW_DEBT_THIS_YEAR);
        }
        setRequiredFields(fields);
    }, [newDebtThisYearToggle]);

    const validationErrors = useValidation(requiredFields);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length > 0) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = Number(value ?? 0) > 0;

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : 'field.applicant.min-value.error',
        };
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.finance.title')} />
            <MonetaryField
                name={NET_MONTHLY_INCOME}
                label={translate('field.applicant-net-monthly-income.label')}
                customValidation={minValueValidation}
            />
            <Box sx={{ mt: 2 }}>
                <Subtitle text={translate('field.applicant-new-debt-this-year.label')} />
                <RadioField name={NEW_DEBT_THIS_YEAR_TOGGLE} options={yesNoOptions} />
            </Box>
            {newDebtThisYearToggle && (
                <Box sx={{ mt: 1.5 }}>
                    <MonetaryField
                        name={NEW_DEBT_THIS_YEAR}
                        label={translate('field.applicant-new-debt-this-year-amount.label')}
                        customValidation={minValueValidation}
                    />
                </Box>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantFinancePageDK;
